import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { fetchCartState, runCartMutation, runCartRequest } from '@theme/skin-elite-ebook-gift';
import { createCartLineIdentity, cartLineIdentity } from './skin-elite-cart-line.js?v=fc89a00c8f482b52';

const bundleQuantities = [1, 4, 6];

export const getOneTimeBalmLines = (cart) => (cart?.items || []).filter(
  (item) => item.handle === 'skin-elite-miracle-balm' && !item.selling_plan_allocation
);

export const getNextBundleQuantity = (quantity, direction) => {
  const currentQuantity = Math.max(0, Math.trunc(Number(quantity) || 0));
  if (direction < 0) return bundleQuantities.filter((value) => value < currentQuantity).at(-1) ?? currentQuantity;
  if (direction > 0) return bundleQuantities.find((value) => value > currentQuantity) ?? currentQuantity;
  return currentQuantity;
};

export const planBundleAction = (cart, action) => {
  const lines = getOneTimeBalmLines(cart);
  const currentQuantity = lines.reduce((total, item) => total + item.quantity, 0);
  const nextQuantity = action.kind === 'remove' ? 0 : action.kind === 'legacyRepair'
    ? (currentQuantity === 3 ? 6 : currentQuantity)
    : getNextBundleQuantity(currentQuantity, action.direction);
  return {
    currentQuantity, nextQuantity, changed: lines.length > 0 && nextQuantity !== currentQuantity,
    updates: Object.fromEntries(lines.map((item, index) => [item.key, index === 0 ? nextQuantity : 0])),
  };
};

export const applyBundleAction = async ({ fetchCart, postCart }, action) => {
  // Calculate direction from fresh stock quantities inside the shared mutation queue.
  // A stale drawer must never contribute a physical-quantity delta or obsolete line key.
  const cart = await fetchCart();
  const plan = planBundleAction(cart, action);
  if (!plan.changed) return cart;
  // One physical POST only. If its response is uncertain, the caller refreshes read-only.
  return postCart(plan.updates);
};

const bambooProductId = 9684399816793;
const changedBambooCart = () => new Error('Your Bamboo bundle changed. Please refresh your cart and try again.');

export const getOneTimeBambooLines = (cart) => (cart?.items || []).filter(
  (item) => Number(item.product_id) === bambooProductId && !item.selling_plan_allocation && !item.selling_plan
);

// Discounts may split one identity across several keys. Compare the complete
// selection and summed quantities, while resolving only the current native keys.
export const bambooSelectionSignature = (lines, snapshot = false) => {
  if (!Array.isArray(lines) || !lines.length) throw changedBambooCart();
  const quantities = new Map();
  for (const line of lines) {
    const variantId = snapshot ? line?.variantId : line?.variant_id ?? line?.id;
    const quantity = Number(line?.quantity);
    // Liquid's json filter renders a properties hash as [[key, value], ...] ([] when empty).
    const properties = Array.isArray(line?.properties) &&
      line.properties.every((entry) => Array.isArray(entry) && entry.length === 2)
      ? Object.fromEntries(line.properties) : line?.properties;
    if (!variantId || !Number.isSafeInteger(quantity) || quantity <= 0 ||
      (properties != null && (typeof properties !== 'object' || Array.isArray(properties)))) {
      throw changedBambooCart();
    }
    const identity = snapshot ? createCartLineIdentity({
      variantId, properties: properties || {}, sellingPlanId: line.sellingPlanId || '',
    }) : cartLineIdentity(line);
    quantities.set(identity, (quantities.get(identity) || 0) + quantity);
  }
  return JSON.stringify([...quantities].sort(([left], [right]) => left.localeCompare(right)));
};

export const planBambooGroupAction = (cart, action) => {
  if (!['bundle', 'bonus'].includes(action?.scope)) throw changedBambooCart();
  const lines = getOneTimeBambooLines(cart);
  const targets = action.scope === 'bonus'
    ? lines.filter((item) => item.properties?._skin_elite_bamboo_gift === 'v1') : lines;
  // A second click after an already-confirmed removal needs no physical write.
  if (!targets.length) return { changed: false, updates: {} };
  if (bambooSelectionSignature(lines) !== bambooSelectionSignature(action.expectedLines, true)) {
    throw changedBambooCart();
  }
  const keys = new Set();
  for (const item of targets) {
    if (typeof item.key !== 'string' || !item.key.trim() || keys.has(item.key) ||
      item.instructions?.can_remove === false || item.parent_relationship?.parent) {
      throw changedBambooCart();
    }
    keys.add(item.key);
  }
  return { changed: true, updates: Object.fromEntries(targets.map((item) => [item.key, 0])) };
};

export const applyBambooGroupAction = async ({ fetchCart, postCart }, action) => {
  const cart = await fetchCart();
  const plan = planBambooGroupAction(cart, action);
  if (!plan.changed) return cart;
  // Never replay a write after an uncertain response. The UI only reads back.
  return postCart(plan.updates);
};

const getCartRoutes = () => {
  const root = window.Shopify?.routes?.root || '/';
  return { update: window.Theme?.routes?.cart_update_url || `${root}cart/update.js` };
};

const getSectionIds = () => Array.from(document.querySelectorAll('cart-items-component'))
  .map((component) => component.dataset.sectionId)
  .filter((id, index, ids) => id && ids.indexOf(id) === index);

const postBundleUpdates = (updates, sections) => runCartRequest(async (signal) => {
  const response = await fetch(getCartRoutes().update, {
    signal,
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      updates,
      sections: sections.slice(0, 5).join(','),
      sections_url: window.location.pathname,
    }),
  });
  const updatedCart = await response.json();
  if (!response.ok || updatedCart.errors) {
    throw new Error(updatedCart.description || updatedCart.message || updatedCart.errors || 'Cart update failed.');
  }
  return updatedCart;
});

const createCartResult = (cart, didError = false) => ({
  cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
  detail: {
    sections: cart.sections, items: cart.items, itemCount: cart.item_count,
    source: 'skin-elite-cart-bundle-groups', didError,
  },
});

if (typeof window !== 'undefined' && typeof document !== 'undefined' && !window.__skinEliteCartBundleGroups) {
  window.__skinEliteCartBundleGroups = true;

  const runBundleAction = (action, sections) => runCartMutation(() => applyBundleAction({
    fetchCart: fetchCartState,
    postCart: (updates) => postBundleUpdates(updates, sections),
  }, action), { sectionIds: sections });

  let bundleActionPending = false;
  const updateBundleQuantity = async (row, action) => {
    if (bundleActionPending || row.dataset.bundleBusy === 'true') return;
    bundleActionPending = true;
    const component = row.closest('cart-items-component');
    const controls = new Map(Array.from(row.querySelectorAll('button, input, select')).map((control) => [control, control.disabled]));
    const sections = getSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});

    row.dataset.bundleBusy = 'true';
    row.setAttribute('aria-busy', 'true');
    controls.forEach((_disabled, control) => { control.disabled = true; });
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: action.kind === 'remove' ? 'remove' : 'update', context: 'cart', lines: [], promise: deferred.promise,
    }));

    try {
      deferred.resolve(createCartResult(await runBundleAction(action, sections)));
    } catch (error) {
      // Never replay a physical mutation after an uncertain response or failed gift sync.
      try {
        deferred.resolve(createCartResult(await fetchCartState(), true));
      } catch (refreshError) {
        deferred.reject(refreshError);
      }
      document.dispatchEvent(new CartErrorEvent({
        error: error?.message || 'Cart update failed.', code: 'SERVICE_UNAVAILABLE',
      }));
    } finally {
      bundleActionPending = false;
      // A server morph may replace the row or already restore its controls.
      // Never unlock another row's pending operation or override fresh bounds.
      if (row.dataset.bundleBusy === 'true') {
        row.dataset.bundleBusy = 'false';
        row.removeAttribute('aria-busy');
        controls.forEach((disabled, control) => {
          if (component?.contains(control)) control.disabled = disabled;
        });
      }
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[data-bundle-step], [data-bundle-remove]');
    const row = button?.closest('[data-skin-elite-bundle-group]');
    if (!button || !row || button.disabled) return;
    event.preventDefault();
    const action = button.hasAttribute('data-bundle-remove') ? { kind: 'remove' }
      : { kind: 'step', direction: Number(button.dataset.bundleStep || 0) };
    updateBundleQuantity(row, action);
  });

  // Earlier cart controls could leave three paid jars without the three free jars.
  // Repair that exact legacy state once per page; other custom quantities stay intact.
  const repairLegacyBundle = async () => {
    if (!document.querySelector('[data-skin-elite-bundle-group][data-bundle-quantity="3"]')) return;
    const sections = getSectionIds();
    let repaired = false;
    try {
      const cart = await runCartMutation(() => applyBundleAction({
        fetchCart: fetchCartState,
        postCart: (updates) => {
          repaired = true;
          return postBundleUpdates(updates, sections);
        },
      }, { kind: 'legacyRepair' }), { sectionIds: sections });
      if (!repaired) return;
      if (document.querySelector('cart-items-component') && typeof customElements !== 'undefined') {
        await customElements.whenDefined('cart-items-component');
      }
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'update', context: 'cart', lines: [], promise: Promise.resolve(createCartResult(cart)),
      }));
    } catch (error) {
      // The queue records this failed attempt; do not resubmit the physical POST.
      if (repaired) {
        try {
          const cart = await fetchCartState();
          document.dispatchEvent(new CartLinesUpdateEvent({
            action: 'update', context: 'cart', lines: [], promise: Promise.resolve(createCartResult(cart, true)),
          }));
        } catch (_refreshError) { /* The native error below keeps checkout reviewable. */ }
      }
      document.dispatchEvent(new CartErrorEvent({
        error: error?.message || 'Your bundle could not be confirmed. Please refresh your cart.', code: 'SERVICE_UNAVAILABLE',
      }));
    }
  };
  repairLegacyBundle();
}

// A new cart section can arrive on a page that already installed the older Balm module.
if (typeof window !== 'undefined' && typeof document !== 'undefined' && !window.__skinEliteBambooCartGroups) {
  window.__skinEliteBambooCartGroups = true;

  const runBambooGroupAction = (action, sections) => runCartMutation(() => applyBambooGroupAction({
    fetchCart: fetchCartState,
    postCart: (updates) => postBundleUpdates(updates, sections),
  }, action), { sectionIds: sections });

  let bambooActionPending = false;
  const removeBambooGroup = async (row, scope) => {
    if (bambooActionPending || row.dataset.bambooBusy === 'true') return;
    bambooActionPending = true;
    const rows = Array.from(document.querySelectorAll('[data-bamboo-cart-group]'));
    const controls = new Map(rows.flatMap((item) => Array.from(item.querySelectorAll('button, input, select')))
      .map((control) => [control, control.disabled]));
    const sections = getSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(() => {});

    rows.forEach((item) => {
      item.dataset.bambooBusy = 'true';
      item.setAttribute('aria-busy', 'true');
    });
    controls.forEach((_disabled, control) => { control.disabled = true; });
    // Publish before starting the queued operation so checkout guards await it.
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'remove', context: 'cart', lines: [], promise: deferred.promise,
    }));

    try {
      const expectedLines = JSON.parse(row.dataset.bambooCartLines || 'null');
      const cart = await runBambooGroupAction({ scope, expectedLines }, sections);
      deferred.resolve(createCartResult(cart));
    } catch (error) {
      try {
        deferred.resolve(createCartResult(await fetchCartState(), true));
      } catch (refreshError) {
        deferred.reject(refreshError);
      }
      document.dispatchEvent(new CartErrorEvent({
        error: error?.message || 'Cart update failed.', code: 'SERVICE_UNAVAILABLE',
      }));
    } finally {
      bambooActionPending = false;
      rows.forEach((item) => {
        if (item.dataset.bambooBusy !== 'true') return;
        delete item.dataset.bambooBusy;
        item.removeAttribute('aria-busy');
      });
      controls.forEach((disabled, control) => {
        if (control.isConnected) control.disabled = disabled;
      });
    }
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[data-bamboo-group-remove]');
    const row = button?.closest('[data-bamboo-cart-group]');
    if (!row || button.disabled) return;
    event.preventDefault();
    void removeBambooGroup(row, button.dataset.bambooGroupRemove);
  });
}
